import type { ViewReference } from '@cognite/sdk';

import {
  CDM_3D_CONTAINER_EXTERNAL_IDS,
  CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
  CDM_ASSET_MODELS_QUERY_RELATIONS,
  CDM_ASSET_MODELS_QUERY_STEPS,
} from '../../../constants/cdm-infield-data-model';
import { CDM_3D_CONTAINER_SPACE } from '../../../providers/fdm-services/consts';
import type { ThreeDServiceViewReferences } from '../cdm-3d-service';

export const getCdmAssetModelsQuery = (
  assetExternalId: string,
  assetViewReference: ViewReference,
  viewReferences: ThreeDServiceViewReferences
) => {
  // Here we assume that all of the core views are in the same space

  return {
    with: {
      [CDM_ASSET_MODELS_QUERY_STEPS.assets]: {
        limit: 50,
        nodes: {
          filter: {
            and: [
              {
                matchAll: {},
              },
              {
                hasData: [assetViewReference],
              },
              {
                or: [
                  {
                    equals: {
                      property: ['node', 'externalId'],
                      value: assetExternalId,
                    },
                  },
                ],
              },
            ],
          },
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.object3d]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.assets,
          through: {
            source: assetViewReference,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.object3D,
          },
          direction: 'outwards',
          filter: {
            and: [
              {
                matchAll: {},
              },
              {
                hasData: [
                  {
                    type: 'container',
                    space: CDM_3D_CONTAINER_SPACE,
                    externalId: CDM_3D_CONTAINER_EXTERNAL_IDS.Cognite3DObject,
                  },
                ],
              },
            ],
          },
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.cadNodes]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.object3d,
          through: {
            source: viewReferences.cdmCadNode,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.object3D,
          },
          direction: 'inwards',
          filter: {
            and: [
              {
                matchAll: {},
              },
              {
                hasData: [
                  {
                    type: 'container',
                    space: CDM_3D_CONTAINER_SPACE,
                    externalId: CDM_3D_CONTAINER_EXTERNAL_IDS.CogniteCADNode,
                  },
                ],
              },
            ],
          },
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.model3D]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.cadNodes,
          through: {
            source: viewReferences.cdmCadNode,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.model3D,
          },
          direction: 'outwards',
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.revisions]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.cadNodes,
          through: {
            source: viewReferences.cdmCadNode,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.revisions,
          },
          direction: 'outwards',
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.pointCloudVolumes]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.object3d,
          through: {
            source: viewReferences.cdmPointCloudVolume,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.object3D,
          },
          direction: 'inwards',
          filter: {
            and: [
              {
                matchAll: {},
              },
              {
                hasData: [
                  {
                    type: 'container',
                    space: CDM_3D_CONTAINER_SPACE,
                    externalId:
                      CDM_3D_CONTAINER_EXTERNAL_IDS.CognitePointCloudVolume,
                  },
                ],
              },
            ],
          },
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.pcModel3D]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.pointCloudVolumes,
          through: {
            source: viewReferences.cdmPointCloudVolume,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.model3D,
          },
          direction: 'outwards',
        },
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.pcRevisions]: {
        limit: CDM_ASSET_MODELS_DMS_GRAPH_NODE_LIMIT,
        nodes: {
          from: CDM_ASSET_MODELS_QUERY_STEPS.pointCloudVolumes,
          through: {
            source: viewReferences.cdmPointCloudVolume,
            identifier: CDM_ASSET_MODELS_QUERY_RELATIONS.revisions,
          },
          direction: 'outwards',
        },
      },
    },
    select: {
      [CDM_ASSET_MODELS_QUERY_STEPS.model3D]: {
        sources: [
          {
            source: viewReferences.cdm3dModel,
            properties: ['*'],
          },
        ],
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.revisions]: {
        sources: [
          {
            source: viewReferences.cdmCadRevision,
            properties: ['*'],
          },
        ],
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.pcModel3D]: {
        sources: [
          {
            source: viewReferences.cdm3dModel,
            properties: ['*'],
          },
        ],
      },
      [CDM_ASSET_MODELS_QUERY_STEPS.pcRevisions]: {
        sources: [
          {
            source: viewReferences.cdmPointCloudRevision,
            properties: ['*'],
          },
        ],
      },
    },
  };
};
